/**
 * bind-client-pagination.js — 前台列表 client 端分頁（切片重繪 + URL 同步）
 *
 * 把一個列表容器（listEl）的直屬 <li> 依 pageSize 切片，只顯示當前頁；點頁碼即時重繪
 * list + pager，不 reload。吃全站共用 buildPaginationItems（省略號規則與後台一致），
 * 視覺沿用 pagination.css atom（不重做）。compact（≤768px）切 .pagination--compact。
 *
 * 與篩選共存（articles 的 category chip + 搜尋）：
 *   pagination 是「最終可見性」的單一擁有者（owns .hidden）。外部篩選不直接動 .hidden，
 *   而以 isEligible(li) 回報「這張卡是否通過篩選」；篩選變動後呼叫 paginator.refresh()，
 *   pagination 重算「合格集合」的頁數並切回第 1 頁。避免兩套系統搶寫 .hidden 衝突
 *   （見專案 memory：hidden display 守衛 / reveal 純加法）。
 *
 * reveal 安全（關鍵）：
 *   被分頁顯示的卡多在 below-fold，其 [data-reveal] 初始 opacity:0，僅 .is-revealed 才顯。
 *   IntersectionObserver 一次性觀察，換頁後新顯卡可能永卡不可見。故每次顯示當前頁卡片時，
 *   主動對「卡片自身 + 其 [data-reveal] 後代」補 .is-revealed（冪等、純加法），保證不卡隱形。
 *
 * URL 同步：history.replaceState('?page=N')，可分享 / 可前進但不污染 history stack（不 reload）。
 * 初始讀 ?page=N（越界自動夾回 [1, pageCount]）。
 *
 * 設計：root 守門早退（缺 listEl / pagerEl / 無 item 即 return null）；cleanup 走 pagehide。
 */

import { buildPaginationItems, renderItemLink, renderCompact } from './pagination.js';

// 768px 與 pagination.css 觸控放大斷點（≤768 item/ellipsis 升 44×44）統一。
// 原 640px 造成 641–768px 中間壞態（放大版數字頁碼卻未切 compact）。
const COMPACT_MQ = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(max-width: 768px)')
  : null;

// 「返回列表回到點擊處」還原窗口（referrer 為主閘、TTL 為次要保險）：iOS bfcache-miss 常把
// 「返回」誤報成 navigate（WebKit 已知不一致）→ navigate 分支需 referrer + TTL 雙保險才救援，
// 避免「直接新開 /clinics?page=2」被誤判成返回而幽靈捲動。30 分鐘。
const LIST_RETURN_TTL_MS = 1800000;

// referrer 被 iOS Safari 剝離（document.referrer 為空字串，new URL('') 會 throw 被 referrerMatch
// 吞成 false）時的短寬限備援：navigate 分支主判定（referrer 完整匹配 + 完整 TTL）失效時，退而只
// 看「離開到現在多快」。窗口刻意遠短於 LIST_RETURN_TTL_MS（30 分鐘），限制「使用者離開列表後在
// 別處逛了一陣子、又從別的入口重新進入同一頁面」被誤判成返回的風險視窗——10 秒內基本只可能是
// 「剛點出去、referrer 卻沒能正確帶回來」這類真實返回，而非巧合重訪。
const SHORT_GRACE_MS = 10000;

/** 把卡片自身與其所有 [data-reveal] 後代補成已顯示態（冪等、純加法，避免分頁卡隱形）。 */
function forceReveal(li) {
  if (li.hasAttribute('data-reveal')) li.classList.add('is-revealed');
  li.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => el.classList.add('is-revealed'));
}

/**
 * 綁定 client 端分頁。
 *
 * @param {HTMLElement} listEl 列表容器（其直屬 <li> 為分頁單位）
 * @param {HTMLElement} pagerEl 分頁 nav 容器（innerHTML 由本控制器接管）
 * @param {Object} opts
 * @param {number}  opts.pageSize 每頁筆數（必填，>0）
 * @param {(li:HTMLElement)=>boolean} [opts.isEligible] 卡片是否通過外部篩選（預設全通過）
 * @param {string}  [opts.param='page'] URL query 參數名
 * @param {boolean} [opts.syncUrl=true] 是否 history.replaceState 同步 ?page=N
 * @param {(page:number, pageCount:number)=>void} [opts.onPageChange] 換頁後回呼（如捲到列表頂）
 * @returns {{refresh:()=>void, goToPage:(n:number)=>void, getState:()=>{page:number,pageCount:number}, destroy:()=>void}|null}
 */
export function bindClientPagination(listEl, pagerEl, opts = {}) {
  if (!listEl || !pagerEl) return null;
  const pageSize = opts.pageSize;
  if (!pageSize || pageSize < 1) return null;

  const allItems = Array.from(listEl.querySelectorAll(':scope > li'));
  if (!allItems.length) return null;

  const isEligible = typeof opts.isEligible === 'function' ? opts.isEligible : () => true;
  const param = opts.param || 'page';
  const syncUrl = opts.syncUrl !== false;

  let currentPage = readPageFromUrl();

  /** 讀 URL ?page=N（無 / 非數字 → 1；夾回 [1,∞)，上界由 render 時的 pageCount 再夾）。 */
  function readPageFromUrl() {
    const raw = new URLSearchParams(location.search).get(param);
    const n = parseInt(raw, 10);
    return Number.isNaN(n) || n < 1 ? 1 : n;
  }

  /** 目前合格（通過篩選）的卡片陣列。 */
  function eligibleItems() {
    return allItems.filter((li) => isEligible(li));
  }

  /** 寫 ?page=N 進 URL（replaceState 不 reload、不堆 history）。page===1 時移除參數保乾淨。 */
  function writeUrl(page) {
    if (!syncUrl) return;
    const url = new URL(location.href);
    if (page <= 1) url.searchParams.delete(param);
    else url.searchParams.set(param, String(page));
    history.replaceState(history.state, '', url);
  }

  /** 切片 + 顯隱 + 重繪 pager。currentPage 越界先夾回。 */
  function render() {
    const eligible = eligibleItems();
    const pageCount = Math.max(1, Math.ceil(eligible.length / pageSize));
    if (currentPage > pageCount) currentPage = pageCount;
    if (currentPage < 1) currentPage = 1;

    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    const pageSet = new Set(eligible.slice(start, end));

    // 單一擁有者：非合格 → 一律隱藏；合格但不在當前頁 → 隱藏；當前頁 → 顯示 + 補 reveal。
    allItems.forEach((li) => {
      const show = pageSet.has(li);
      li.hidden = !show;
      if (show) forceReveal(li);
    });

    paintPager(pageCount);
  }

  /** 上漆 pager：compact（≤768）→ ‹ cur/total ›；standard → buildPaginationItems。單頁清空 + hidden。 */
  function paintPager(pageCount) {
    if (pageCount <= 1) {
      // 單頁 → 清空 innerHTML + hidden 整個 <nav>，避免留「具名但 0 子元素」的空 landmark（a11y 雜訊，S-16）。
      // 比照 pages/search.js syncPager 已做對的模式，三頁（clinics/articles/search）行為一致。
      pagerEl.innerHTML = '';
      pagerEl.classList.remove('pagination--compact');
      pagerEl.hidden = true;
      return;
    }
    pagerEl.hidden = false; // 有真分頁才讓 landmark 進 a11y 樹。
    const compact = !!(COMPACT_MQ && COMPACT_MQ.matches);
    pagerEl.classList.toggle('pagination--compact', compact);
    const hrefFor = (p) => {
      const url = new URL(location.href);
      if (p <= 1) url.searchParams.delete(param);
      else url.searchParams.set(param, String(p));
      return `${url.pathname}${url.search}`;
    };
    pagerEl.innerHTML = compact
      ? renderCompact(currentPage, pageCount, { mode: 'link', hrefFor })
      : buildPaginationItems(currentPage, pageCount).map((it) => renderItemLink(it, hrefFor)).join('');
  }

  function goToPage(n) {
    const eligible = eligibleItems();
    const pageCount = Math.max(1, Math.ceil(eligible.length / pageSize));
    const next = Math.min(Math.max(1, n), pageCount);
    if (next === currentPage) return;
    currentPage = next;
    writeUrl(currentPage);
    render();
    if (opts.onPageChange) opts.onPageChange(currentPage, pageCount);
  }

  /** 篩選變動後重置到第 1 頁並重繪（外部篩選器於 onAfterFilter 呼叫）。 */
  function refresh() {
    currentPage = 1;
    writeUrl(currentPage);
    render();
  }

  // pager 點擊：委派一次（攔截 <a> 預設導頁，改 client 換頁；href 仍保留供無 JS / 中鍵開新分頁）。
  // 目標頁直接讀 data-page（renderItemLink / renderCompact 已附），免解析 href。
  function onPagerClick(e) {
    // 中鍵 / Cmd / Ctrl + 點擊 → 放行原生「新分頁開啟」行為（href 即完整 ?page=N 連結）。
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest('[data-page]');
    if (!link || !pagerEl.contains(link)) return;
    if (link.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
    const target = parseInt(link.getAttribute('data-page'), 10);
    if (!Number.isNaN(target)) {
      e.preventDefault();
      goToPage(target);
    }
  }
  pagerEl.addEventListener('click', onPagerClick);

  // 斷點切換：只重漆 pager（不重切片，currentPage 不變）。
  const onMqChange = () => {
    const pageCount = Math.max(1, Math.ceil(eligibleItems().length / pageSize));
    paintPager(pageCount);
  };
  if (COMPACT_MQ) {
    if (COMPACT_MQ.addEventListener) COMPACT_MQ.addEventListener('change', onMqChange);
    else if (COMPACT_MQ.addListener) COMPACT_MQ.addListener(onMqChange);
  }

  // ── 返回列表回到點擊處（list-return scroll restoration）──────────────────────
  // arm：點卡離開的當下（click，同步早於導航）寫一筆錨定 record；restore：列表頁載入後跑一次，
  // 把離開前點擊的卡片還原到「同一視窗偏移」。全程 instant（顯式 behavior:'instant'——全域
  // html{scroll-behavior:smooth} 下 2-arg scrollTo / behavior:'auto' 會繼承 smooth，故必須顯式），永不 smooth。
  // main.js:88-93 的通用 scrollY 還原保留不動，本段只在列表頁「疊加覆蓋」。
  //
  // key 用 pathname-only（不含 search）：H2 指出返回時 search 可能掉字，pathname-only 才穩定命中；
  // 一個列表頁一筆，後點覆蓋前點。與 main.js 的通用 key（scrollY: + pathname + search）刻意分開。
  const listReturnKey = () => 'listReturn:' + location.pathname;

  // arm：命中「會導航離開的卡片連結」時寫 record（掛在 listEl，與 pagerEl 的 pager 委派分開）。
  function onCardListClick(e) {
    // 修飾鍵 / 非左鍵 = 新分頁開啟，不 arm（對齊 onPagerClick 準則，見上方 line 154）。
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest('a[href]');
    if (!link) return;
    const li = link.closest('li');
    if (!li) return;
    const index = allItems.indexOf(li);
    if (index < 0) return; // 該卡不屬 allItems（非分頁單位）→ 不 arm。
    let toPath;
    try {
      const dest = new URL(link.href, location.href);
      if (dest.origin !== location.origin) return; // 非同源導航 → 不 arm。
      toPath = dest.pathname;                       // 解析後絕對 pathname（dev /clinic-detail、prod .../dist/clinic-detail.html 皆自動正確）。
    } catch (_) {
      return; // 連結無法解析 → 不 arm。
    }
    try {
      sessionStorage.setItem(listReturnKey(), JSON.stringify({
        v: 1,
        index,                                                 // <li> 在 allItems 的 DOM index（build 靜態、不重排 → 唯一穩定鍵）。
        page: currentPage,                                     // 離開時所在分頁。
        viewportTop: Math.round(li.getBoundingClientRect().top), // 卡片當時距視窗頂 px（可為負）。
        scrollY: Math.round(window.scrollY),                   // fallback 用整頁位移。
        toPath,                                                // 供返回時 referrer 比對。
        armedAt: Date.now(),
      }));
    } catch (_) { /* 私密瀏覽 / 配額 throw → 靜默略過，退化為無錨定 */ }
  }
  listEl.addEventListener('click', onCardListClick);

  /** document.referrer 的 pathname 是否 === 離開時記下的 toPath（跨域 / 空 referrer → false）。 */
  function referrerMatch(toPath) {
    try { return new URL(document.referrer).pathname === toPath; }
    catch (_) { return false; }
  }

  /** instant 捲動：顯式 behavior:'instant'。全域 reset.css html{scroll-behavior:smooth} 下，2-arg
   *  scrollTo 與 behavior:'auto' 都會「繼承 smooth 動畫」；還原捲動必須瞬時無 easing，故一律顯式。 */
  function scrollToInstant(y) {
    window.scrollTo({ top: Math.max(0, y), behavior: 'instant' });
  }

  /** 用「當前 rect」把 target 錨回離開前同一視窗偏移（instant）；target 不在 / 隱藏則 no-op。 */
  function positionToCard(target, viewportTop) {
    if (!target || target.hidden) return;
    scrollToInstant(window.scrollY + target.getBoundingClientRect().top - viewportTop);
  }

  // 沉澱校正控制代號：進行中可被 destroy / 重入中止，避免殘留 listener / rAF / timer。
  let cancelSettle = null;

  // 錨定 + 沉澱期二次校正：先立即錨定一次，再於「上方 lazy 圖 / 字型 late-load / 容器 reflow」造成
  // CLS 時，用當前 rect 重新錨定（self-correcting），直到沉澱窗（SETTLE_MS）結束或使用者主動捲動。
  // 修 quality S1 FAIL 根因——單次 rAF 錨定早於 async 圖載完成，落點偏移且 racy（±112px、同操作三趟兩值）。
  function settleAnchor(target, viewportTop) {
    positionToCard(target, viewportTop);   // best-effort 立即錨定。
    if (cancelSettle) cancelSettle();      // 防重入：中止任何進行中的沉澱。

    const SETTLE_MS = 1500;                // 沉澱窗上限：CLS 收斂窗；逾時保證清乾淨。全程不阻擋使用者。
    const USER_INTENT = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    const listenOpts = { passive: true, capture: true };
    let done = false;
    let ro = null;
    let rafId = 0;
    let deadlineId = 0;
    let burst = 6;                         // 開場數幀連續校正，收即時 reflow（不必等事件）。

    const reanchor = () => { if (!done) positionToCard(target, viewportTop); };

    // img / source / iframe 的 load 事件不冒泡 → 於 window 用 capture 攔截；命中資產才重新錨定。
    function onAssetLoad(e) {
      const el = e.target;
      if (el && (el.tagName === 'IMG' || el.tagName === 'SOURCE' || el.tagName === 'IFRAME')) reanchor();
    }

    function burstTick() {
      if (done) return;
      positionToCard(target, viewportTop);
      rafId = --burst > 0 ? requestAnimationFrame(burstTick) : 0;
    }

    function stop() {
      if (done) return;
      done = true;
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(deadlineId);
      window.removeEventListener('load', onAssetLoad, true);
      if (ro) ro.disconnect();
      USER_INTENT.forEach((t) => window.removeEventListener(t, stop, listenOpts));
      if (cancelSettle === stop) cancelSettle = null;
    }

    rafId = requestAnimationFrame(burstTick);
    window.addEventListener('load', onAssetLoad, true);
    if (typeof ResizeObserver === 'function') { ro = new ResizeObserver(reanchor); ro.observe(listEl); }
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(reanchor).catch(() => {}); }
    // 使用者一有捲動意圖立即收手，交還控制權（絕不與使用者互搶）。
    USER_INTENT.forEach((t) => window.addEventListener(t, stop, listenOpts));
    deadlineId = setTimeout(stop, SETTLE_MS);

    cancelSettle = stop;
  }

  // restore：window.load 或 pageshow(persisted=true) 後各自 rAF 跑一次——rAF-after-事件保證
  // 「晚於」main.js 同步 load / pageshow handler（避免被其 scrollTo 蓋掉），且在 layout 後、paint
  // 前執行 → 無中間「閃一下頂端」。bfcacheRestore 參數意義見下方 isReturn 判定註解。
  function runRestore(bfcacheRestore) {
    let record;
    try {
      const raw = sessionStorage.getItem(listReturnKey());
      if (!raw) return;          // 無 record → 交回 main.js 通用還原。
      record = JSON.parse(raw);
    } catch (_) {
      return;                    // 讀 / parse 失敗 → 交回 main.js。
    }
    // one-shot：不論下方是否採用，一律先刪，杜絕二次觸發與日後幽靈。
    try { sessionStorage.removeItem(listReturnKey()); } catch (_) { /* 忽略 */ }
    if (!record || record.v !== 1) return;

    const navType = performance.getEntriesByType('navigation')[0]?.type;
    // isReturn 判定：bfcacheRestore(來自 pageshow 的 e.persisted)是瀏覽器對「這確實是 bfcache
    // 還原」的直接確認，比 navType/referrer 推斷更可靠——bfcache 還原時 document.referrer 凍結
    // 在本頁「第一次載入」當下的值，不會更新成離開前的來源頁，referrer 推斷在此情境註定失準；
    // 為 true 時直接判定 isReturn，略過下方 navType 分支與 TTL/referrer 檢查(那些檢查是為了補償
    // 「不確定是否真是返回」的推斷風險，persisted 已排除該不確定性，不應再受 TTL 限制)。
    // 其餘(bfcacheRestore 為 false，即經由 load 觸發)維持原判定：back_forward 為桌機 / Android /
    // iOS reload-back 常態；navigate 分支為 iOS bfcache-miss 救援(referrer 完整匹配 + 完整 TTL 內，
    // 或 referrer 被剝離時的短寬限備援)；reload 一律 false(交回 main.js 保位)。
    const isReturn = bfcacheRestore
      || navType === 'back_forward'
      || (navType === 'navigate'
        && (Date.now() - record.armedAt) <= LIST_RETURN_TTL_MS
        && referrerMatch(record.toPath))
      || (navType === 'navigate'
        && !referrerMatch(record.toPath)
        && (Date.now() - record.armedAt) <= SHORT_GRACE_MS);
    if (!isReturn) return; // 非返回 → 不定位（record 已刪，防幽靈）；位置交回 main.js。

    // 先確保分頁正確（防 search 掉字：URL 無 ?page 但 record 記得）——走 init 的靜態 render 路徑，
    // 不呼叫 goToPage、不觸發 onPageChange、不捲動。常態下 URL 已帶正確頁，此步 no-op。
    if (Number.isInteger(record.page) && record.page !== currentPage) {
      currentPage = record.page;
      render();
      writeUrl(currentPage);
    }

    const target = allItems[record.index];
    if (target && !target.hidden) {
      // 主路徑：卡片錨定 + 沉澱期二次校正。單次 rAF 錨定只吸收「rAF 之前」的高度變動；跳到卡片後
      // 才觸發上方 loading=lazy 圖載入 → CLS 反過來推移卡片 → 落點偏移且 racy（quality S1 FAIL 根因）。
      // settleAnchor 於每次資產 late-load / reflow 用當前 rect 重新錨定，把 CLS 逐步吸收乾淨。instant。
      settleAnchor(target, record.viewportTop);
    } else if (Number.isFinite(record.scrollY)) {
      scrollToInstant(record.scrollY); // fallback：卡片對不上（資料變動 / index 越界 / 頁不符）→ 純像素還原。
    }
    // else：什麼都不做（graceful，留在 main.js 的結果）。
  }
  const onLoadRestore = () => requestAnimationFrame(() => runRestore(false));
  window.addEventListener('load', onLoadRestore);

  // bfcache 還原(iOS 常見，尤其記憶體壓力下)不觸發 load，只觸發 pageshow + persisted=true——若只
  // 掛 load，凡是 bfcache 成功保留頁面的情境，record 永遠沒有機會被讀取(bfcache HIT 時 load 根本
  // 不會 fire)，只能仰賴瀏覽器原生還原，settleAnchor 也無從補正還原後 late-load 造成的偏移。兩者
  // 互斥(bfcache HIT 時 load 不 fire)，故 record 不會被重複消費，one-shot 語意仍然成立。rAF 包裹
  // 理由同上：保證晚於 main.js 同步 pageshow handler。fresh load 的 pageshow 也會觸發但 persisted
  // 為 false，此時完全略過，避免跟 load 監聽重複執行同一段還原。
  //
  // 注意：runRestore 一律包一層箭頭函式再交給 requestAnimationFrame——rAF callback 的第一個參數是
  // timestamp，若直接寫 requestAnimationFrame(runRestore)，timestamp 會被當成 bfcacheRestore 參數
  // （非 0 數值恆為 truthy），讓每次 load 觸發的還原都被誤判成 bfcache 還原、略過 navType 判定。
  const onPageShowRestore = (e) => {
    if (!e.persisted) return;
    requestAnimationFrame(() => runRestore(true));
  };
  window.addEventListener('pageshow', onPageShowRestore);

  function destroy() {
    if (cancelSettle) cancelSettle(); // 頁面卸載時中止進行中的沉澱校正，清乾淨 listener / rAF / timer。
    pagerEl.removeEventListener('click', onPagerClick);
    listEl.removeEventListener('click', onCardListClick);
    window.removeEventListener('load', onLoadRestore);
    // 注意：onPageShowRestore 刻意不在此移除。destroy() 掛在 pagehide（{ once: true }，故只會執行
    // 一次），而 pagehide 在「真正卸載」與「bfcache 凍結」兩種情境都會觸發——若跟著拔掉 pageshow
    // listener，凍結當下就把它移除，之後真正 thaw 時 pageshow 已經沒人聽，等於讓上面的第二入口
    // 修復形同虛設。真正卸載時整份文件連同其所有 listener 都會被瀏覽器回收，不移除也無洩漏風險。
    if (COMPACT_MQ) {
      if (COMPACT_MQ.removeEventListener) COMPACT_MQ.removeEventListener('change', onMqChange);
      else if (COMPACT_MQ.removeListener) COMPACT_MQ.removeListener(onMqChange);
    }
  }
  window.addEventListener('pagehide', destroy, { once: true });

  render();

  return {
    refresh,
    goToPage,
    getState: () => ({
      page: currentPage,
      pageCount: Math.max(1, Math.ceil(eligibleItems().length / pageSize)),
    }),
    destroy,
  };
}
